import { useEffect, useRef, useState } from "react";
import { useRoute } from "wouter";
import AppShell from "@/components/foundations/core/AppShell/AppShell";
import { TaskLogEditor } from "@/components/tasks/details/TaskLogEditor/TaskLogEditor";
import { trpc } from "@/lib/trpc";
import { DEFAULT_AREA_COLOR, ListColorKey, resolveTaskColors } from "@/lib/listColors";
import { toast } from "sonner";

export default function TaskLog() {
  const [, params] = useRoute<{ id: string }>("/task/:id/log");
  const id = params ? parseInt(params.id, 10) : 0;
  const utils = trpc.useUtils();
  const taskQuery = trpc.tasks.get.useQuery({ id }, { enabled: id > 0 });
  const [logText, setLogText] = useState("");
  const hydratedKey = useRef<number | null>(null);

  useEffect(() => {
    if (!taskQuery.data) return;
    if (hydratedKey.current === taskQuery.data.id) return;
    hydratedKey.current = taskQuery.data.id;
    setLogText(taskQuery.data.log ?? "");
  }, [taskQuery.data]);

  const updateMutation = trpc.tasks.update.useMutation({
    onSuccess: () => {
      utils.tasks.get.invalidate({ id });
      utils.day.list.invalidate();
      utils.day.monthSummary.invalidate();
    },
    onError: e => toast.error(e.message),
  });

  function saveLog() {
    if (!taskQuery.data) return;
    const next = logText.trim() === "" ? null : logText;
    if ((next ?? null) !== ((taskQuery.data.log ?? null) as string | null)) {
      updateMutation.mutate({ id, log: next });
    }
  }

  function clearLog() {
    setLogText("");
  }

  if (!id) return null;
  if (taskQuery.isLoading || !taskQuery.data) {
    return (
      <AppShell backTo={`/task/${id}`} title="Log">
        <div className="container max-w-2xl py-8">
          <div className="h-40 rounded-xl bg-muted/40 animate-pulse" />
        </div>
      </AppShell>
    );
  }

  const t = taskQuery.data;
  const statusColors = resolveTaskColors({
    barColor: (t.barColor ?? null) as ListColorKey | null,
    bgColor: (t.bgColor ?? null) as ListColorKey | null,
    listColor: (t.listColor as string | null) ?? DEFAULT_AREA_COLOR,
    listBgColor: (t as { listBgColor?: string | null }).listBgColor ?? null,
  });

  return (
    <AppShell backTo={`/task/${id}`} title="Log">
      <div className="container max-w-2xl py-8 pb-12">
        <div
          className="bg-card border rounded-xl p-4 space-y-3"
          style={{
            borderColor: `color-mix(in oklch, ${statusColors.bar} 18%, transparent)`,
          }}
        >
          <TaskLogEditor
            value={logText}
            savedValue={taskQuery.data?.log ?? ""}
            onChange={setLogText}
            onClear={clearLog}
            onCancel={() => setLogText(taskQuery.data?.log ?? "")}
            onSave={saveLog}
            isSaving={updateMutation.isPending}
            accent={statusColors}
          />
        </div>
      </div>
    </AppShell>
  );
}
